// src/landing/components/ContactSection.js
import React, { useContext } from 'react';
import {
  MapPin,
  Mail,
  Phone,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
} from 'lucide-react';
import { LanguageContext } from '../../common/contexts/LanguageContext';
import { saveContact } from '../../common/firebase';
import '../styles/ContactSection.css';

export default function ContactSection() {
  const { t } = useContext(LanguageContext);

  // ② 번호 → 한글 매핑 배열
  const subjectLabelsKor = [
    '서비스 이용 문의',
    '결제 및 환불 문의',
    '회원가입 및 계정 문제',
    '기술 지원 및 오류 신고',
    '기능 제안 및 개선 요청',
    '이벤트 및 프로모션 문의',
    '제휴 및 협력 문의',
    '개인정보 관련 문의',
    '이용 약관 관련 문의',
    '기타 문의'
  ];

  const mapSrc =
    'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3243.317259644531!2d127.9508134!3d37.3196096!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x357b1422c432e6e3%3A0x6f1b80d57441fb5e!2z6rK97J6s7JuQ7KeE65OcIOq1rCDtirjtrJTrj5kgMS0yMC04!5e0!3m2!1sko!2skr!4v1719296400000';

  return (
    <section id="contact" className="contact-section">
      <h2 className="title">{t('contactSection.title')}</h2>
      <p className="subtitle">{t('contactSection.subtitle')}</p>

      <div className="contact-grid">
        <form
          className="contact-form"
          onSubmit={async (e) => {
            e.preventDefault();
            const form = e.target;
            if (!form.checkValidity()) {
              form.reportValidity();
              return;
            }

            // ① 폼에서 값 읽기
            const fd = new FormData(form);
            const subjectId = Number(fd.get('subject'));

            // ③ 번호에 대응하는 한글 문자열로 변환
            const data = {
              name:    fd.get('name'),
              email:   fd.get('email'),
              company: fd.get('company') || '',
              phone:   fd.get('phone')   || '',
              subject: subjectLabelsKor[subjectId - 1],
              message: fd.get('message'),
            };

            // ④ Firestore에 저장
            try {
              await saveContact(data);
              alert(t('contactSection.form.alert')); // 성공 알림
              form.reset();                            // 폼 초기화
            } catch (err) {
              console.error(err);
              alert('전송 중 오류가 발생했습니다. 다시 시도해주세요.');
            }
          }}
        >
          <h3>{t('contactSection.form.heading')}</h3>
          <p className="form-desc">{t('contactSection.form.formDesc')}</p>

          <div className="two-col">
            <label>
              <span>
                {t('contactSection.form.labels.name')}
                <span style={{ color: 'var(--primary)' }}>*</span>
              </span>
              <input
                name="name"
                required
                placeholder={t('contactSection.form.placeholders.name')}
              />
            </label>
            <label>
              <span>{t('contactSection.form.labels.company')}</span>
              <input
                name="company"
                placeholder={t('contactSection.form.placeholders.company')}
              />
            </label>
          </div>

          <div className="two-col">
            <label>
              <span>{t('contactSection.form.labels.phone')}</span>
              <input
                name="phone"
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder={t('contactSection.form.placeholders.phone')}
              />
            </label>
            <label>
              <span>
                {t('contactSection.form.labels.email')}
                <span style={{ color: 'var(--primary)' }}>*</span>
              </span>
              <input
                name="email"
                type="email"
                required
                placeholder={t('contactSection.form.placeholders.email')}
              />
            </label>
          </div>

          <label>
            <span>
              {t('contactSection.form.labels.subject')}
              <span style={{ color: 'var(--primary)' }}>*</span>
            </span>
            <select name="subject" required defaultValue="">
              <option value="" disabled>
                {t('contactSection.form.placeholders.subject')}
              </option>
              {/* ① 옵션에 value 속성으로 번호 지정 */}
              {t('contactSection.form.subjectOptions').map((opt, i) => (
                <option key={i} value={i + 1}>{opt}</option>
              ))}
            </select>
          </label>

          <label>
            <span>
              {t('contactSection.form.labels.message')}
              <span style={{ color: 'var(--primary)' }}>*</span>
            </span>
            <textarea
              name="message"
              rows="4"
              required
              placeholder={t('contactSection.form.placeholders.message')}
            />
          </label>

          <label className="agree">
            <input type="checkbox" required />
            <span>{t('contactSection.form.labels.agree')}</span>
          </label>

          <button type="submit" className="send-btn">
            {t('contactSection.form.sendButton')}
          </button>
        </form>

        <aside className="contact-info">
          <h3>{t('contactSection.aside.heading')}</h3>
          <p className="info-desc">{t('contactSection.aside.infoDesc')}</p>

          <ul className="info-list">
            <li>
              <MapPin size={20} />
              <div>
                <strong>{t('contactSection.aside.locationLabel')}</strong>
                <br />
                {t('contactSection.aside.location')}
              </div>
            </li>
            <li>
              <Mail size={20} />
              <div>
                <strong>{t('contactSection.aside.emailLabel')}</strong>
                <br />
                {t('contactSection.aside.email')}
              </div>
            </li>
            <li>
              <Phone size={20} />
              <div>
                <strong>{t('contactSection.aside.callLabel')}</strong>
                <br />
                {t('contactSection.aside.phone')}
              </div>
            </li>
          </ul>

          <p className="sns-label">{t('contactSection.aside.socialLabel')}</p>
          <div className="sns-icons">
            <a href="#"><Facebook size={20} /></a>
            <a href="#"><Instagram size={20} /></a>
            <a href="#"><Twitter size={20} /></a>
            <a href="#"><Youtube size={20} /></a>
          </div>
        </aside>
      </div>

      <div className="map-wrap">
        <iframe
          title={t('contactSection.map.title')}
          src={mapSrc}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
    </section>
  );
}
